import { Directive, ElementRef, OnDestroy, effect, inject, output, input } from '@angular/core';

interface Offset {
  x: number;
  y: number;
}

/**
 * Drags an element by its transform, for the share page's social network buttons (what
 * angular2-draggable did there, with trackPosition off): the element sits at `position`, follows
 * the pointer while it is dragged, emitting each offset it is at (`position` when the drag started
 * plus how far the pointer has moved), and goes back to `position` when it is let go. A `position`
 * that changes during the drag is applied when the drag ends, so the dragged element never jumps
 * away from the pointer. Clicks on the element still reach its buttons.
 */
@Directive({selector: '[appDragBox]', host: {style: 'touch-action: none', '(pointerdown)': 'start($event)'}})
export class DragBoxDirective implements OnDestroy {
  private element = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly position = input<Offset>({ x: 0, y: 0 });
  /** The element's z-index while it is dragged. */
  readonly zIndexMoving = input('');
  readonly movingOffset = output<Offset>();

  private drag: {pointerX: number; pointerY: number; from: Offset; zIndex: string} | null = null;
  private stopListening: (() => void) | null = null;

  constructor() {
    effect(() => {
      const position = this.position();
      if (!this.drag) {
        this.moveTo(position);
      }
    });
  }

  ngOnDestroy(): void {
    this.stopListening?.();
  }

  start(event: PointerEvent): void {
    if (event.button === 2 || this.drag) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    const element = this.element.nativeElement;
    this.drag = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      from: {x: this.position().x, y: this.position().y},
      zIndex: element.style.zIndex,
    };
    const zIndexMoving = this.zIndexMoving();
    if (zIndexMoving) {
      element.style.zIndex = zIndexMoving;
    }
    const move = (moved: PointerEvent) => this.move(moved);
    const end = () => this.end();
    document.addEventListener('pointermove', move, {passive: false});
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
    document.documentElement.addEventListener('mouseleave', end);
    this.stopListening = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
      document.documentElement.removeEventListener('mouseleave', end);
      this.stopListening = null;
    };
  }

  private move(event: PointerEvent): void {
    if (!this.drag) {
      return;
    }
    event.preventDefault();
    const offset = {
      x: this.drag.from.x + event.clientX - this.drag.pointerX,
      y: this.drag.from.y + event.clientY - this.drag.pointerY,
    };
    this.moveTo(offset);
    this.movingOffset.emit(offset);
  }

  private end(): void {
    if (!this.drag) {
      return;
    }
    this.stopListening?.();
    if (this.zIndexMoving()) {
      this.element.nativeElement.style.zIndex = this.drag.zIndex;
    }
    this.drag = null;
    this.moveTo(this.position());
  }

  private moveTo(offset: Offset): void {
    this.element.nativeElement.style.transform = `translate(${Math.round(offset.x)}px, ${Math.round(offset.y)}px)`;
  }
}
